/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source',
			videoToggle: '[data-video-toggle]',
			videoScope: '[data-video-scope]',
			iconPlay: '.mm-video-play-btn__play',
			iconPause: '.mm-video-play-btn__pause'
		},
		classes: {
			videoLoaded: 'mm-video-loaded'
		},
		timing: {
			resizeDebounce: 150
		}
	},

	// Stored resize handler reference for proper cleanup
	_resizeHandler: null,
	_controlsBound: false,

	/**
	 * Lecture automatique autorisée : ni reduced-motion, ni pause demandée par l'utilisateur
	 */
	_canAutoplay(video) {
		if (video.dataset.userPaused === 'true') return false;
		return !window.matchMedia('(prefers-reduced-motion: reduce)').matches || video.dataset.userPlayed === 'true';
	},

	/**
	 * Vidéos pilotées par un bouton : celles de son scope ([data-video-scope]), sinon de son parent
	 */
	_scopeOf(el) {
		return el.closest(this.config.selectors.videoScope) || el.parentElement;
	},

	/**
	 * Synchronise libellé et icône des boutons play/pause d'une vidéo
	 */
	_syncButtons(video) {
		const scope = this._scopeOf(video);
		if (!scope) return;
		const playing = [...scope.querySelectorAll('video')].some((v) => !v.paused);

		scope.querySelectorAll(this.config.selectors.videoToggle).forEach((btn) => {
			btn.setAttribute('aria-label', playing ? btn.dataset.labelPause : btn.dataset.labelPlay);
			const iconPlay = btn.querySelector(this.config.selectors.iconPlay);
			const iconPause = btn.querySelector(this.config.selectors.iconPause);
			if (iconPlay) iconPlay.hidden = playing;
			if (iconPause) iconPause.hidden = !playing;
		});
	},

	/**
	 * Bouton play/pause (WCAG 2.2.2) et respect de prefers-reduced-motion
	 */
	_bindControls() {
		if (this._controlsBound) return;
		this._controlsBound = true;

		document.addEventListener('click', (e) => {
			const btn = e.target.closest(this.config.selectors.videoToggle);
			if (!btn) return;

			const videos = [...this._scopeOf(btn).querySelectorAll('video')];
			const playing = videos.some((v) => !v.paused);

			videos.forEach((video) => {
				video.dataset.userPaused = String(playing);
				video.dataset.userPlayed = String(!playing);
				if (playing) {
					video.pause();
				} else if (video.checkVisibility?.() ?? true) {
					// Seule la variante affichée (desktop ou mobile) est relancée
					video.play().catch((err) =>
						window.log('warn', 'VideoManager', 'Video play failed', { error: err.message })
					);
				}
			});
		});

		// play/pause ne remontent pas : écoute en phase de capture
		document.addEventListener('play', (e) => {
			if (!(e.target instanceof HTMLVideoElement)) return;
			// Autoplay natif (attribut autoplay) bloqué en reduced-motion
			if (!this._canAutoplay(e.target)) {
				e.target.pause();
				return;
			}
			this._syncButtons(e.target);
		}, true);

		document.addEventListener('pause', (e) => {
			if (e.target instanceof HTMLVideoElement) this._syncButtons(e.target);
		}, true);
	},

	/**
	 * Initialize lazy video loading
	 */
	init() {
		this._bindControls();
		window.getElements('video').forEach((video) => {
			if (video.autoplay && !video.paused && !this._canAutoplay(video)) video.pause();
			this._syncButtons(video);
		});

		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		const loadVideo = (video) => {
			if (video.dataset.loaded !== 'true') {
				const source = video.querySelector(this.config.selectors.videoSource);
				if (!source?.dataset.src) return;

				source.src = source.dataset.src;
				video.preload = 'metadata';
				video.dataset.loaded = 'true';

				video.load();
				if (!this._canAutoplay(video)) return;
				video.play().catch((err) =>
					window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message })
				);
			}
		};

		const unloadVideo = (video) => {
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				// Clear source to release memory
				const source = video.querySelector(this.config.selectors.videoSource);
				if (source) {
					source.src = '';
				}
				video.load();
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.matchMedia(`(max-width: ${window.MOBILE_BREAKPOINT}px)`).matches;
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) ||
								(isDesktop && !isMobileView) ||
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling — remove previous handler before adding new one to avoid accumulation
		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
		}
		this._resizeHandler = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', this._resizeHandler);

		window.emit('global:video-manager-initialized');
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	VideoManager.init();
});
